// src/components/BottomNavigation.js
// Navigation en bas de l'écran pour mode mobile/PWA
import React, { useState, useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import CIcon from '@coreui/icons-react'
import {
  cilSpeedometer,
  cilNewspaper,
  cilPeople,
  cilCalendar,
  cilMenu,
  cilUser,
  cilMap,
  cilX,
  cilAccountLogout,
} from '@coreui/icons'
import { useModules } from '../contexts/ModuleContext'
import { useEntitlements } from '../contexts/EntitlementsContext'
import { getFilteredNavigation } from '../_nav'
import './BottomNavigation.css'

const BottomNavigation = () => {
  const location = useLocation()
  const { t } = useTranslation()
  const { isModuleEnabled, hasContributionPeriods } = useModules()
  const { orgType } = useEntitlements()
  const [showMoreMenu, setShowMoreMenu] = useState(false)

  // Récupérer le rôle admin depuis Redux (même logique que AppSidebar)
  const currentUser = useSelector((state) => state.user?.user)
  const isAdmin = useMemo(() => currentUser?.role === 'admin', [currentUser?.role])

  // Tous les items possibles pour la barre principale (par priorité)
  // Les items avec module seront filtrés selon le forfait
  const allMainItems = [
    {
      to: '/publications',
      icon: cilNewspaper,
      label: t('nav:bottom_nav.feed'),
      module: 'posts',
      priority: 1,
    },
    {
      to: '/members',
      icon: cilPeople,
      label: t('nav:bottom_nav.members'),
      module: 'members',
      priority: 2,
    },
    {
      to: '/events',
      icon: cilCalendar,
      label: t('nav:bottom_nav.events'),
      module: 'events',
      priority: 3,
    },
    {
      to: '/map',
      icon: cilMap,
      label: t('nav:bottom_nav.map'),
      module: 'map',
      priority: 4,
    },
    // Items de fallback (sans module) pour compléter si moins de 4 items disponibles
    {
      to: '/profile',
      icon: cilUser,
      label: t('nav:bottom_nav.profile'),
      priority: 5,
      fallback: true, // Utilisé seulement si pas assez d'items avec modules
    },
    {
      to: '/dashboard',
      icon: cilSpeedometer,
      label: t('nav:bottom_nav.dashboard'),
      priority: 6,
      fallback: true,
    },
  ]

  // Construire la barre principale avec exactement 4 items (+ bouton Plus = 5)
  const buildMainItems = () => {
    const TARGET_COUNT = 4
    const result = []
    const usedPaths = new Set()

    // D'abord, ajouter les items avec modules actifs (non fallback)
    const moduleItems = allMainItems
      .filter((item) => !item.fallback && (!item.module || isModuleEnabled(item.module)))
      .sort((a, b) => a.priority - b.priority)

    for (const item of moduleItems) {
      if (result.length >= TARGET_COUNT) break
      result.push(item)
      usedPaths.add(item.to)
    }

    // Si pas assez d'items, compléter avec les fallback
    if (result.length < TARGET_COUNT) {
      const fallbackItems = allMainItems
        .filter((item) => item.fallback && !usedPaths.has(item.to))
        .sort((a, b) => a.priority - b.priority)

      for (const item of fallbackItems) {
        if (result.length >= TARGET_COUNT) break
        result.push(item)
        usedPaths.add(item.to)
      }
    }

    return result
  }

  const filteredMainItems = buildMainItems()
  const mainItemPaths = new Set(filteredMainItems.map((item) => item.to))

  // Menu "Plus" : dérivé de la MÊME source que le sidebar desktop
  // (getFilteredNavigation) → toute entrée navigable de PREMIER NIVEAU y apparaît
  // avec le bon gating i18n/module/org_type/admin, SANS liste à maintenir en
  // double. On retire les items déjà présents dans la barre principale.
  // NB : _nav.js est actuellement 100 % plat (que des CNavItem) ; si des
  // CNavGroup imbriqués étaient ajoutés, il faudrait aplatir avant le filtre.
  const filteredMoreItems = getFilteredNavigation(
    t,
    isModuleEnabled,
    isAdmin,
    hasContributionPeriods,
    orgType,
  ).filter((item) => item.to && !mainItemPaths.has(item.to))

  // Vérifier si le chemin actuel est dans "Plus"
  const isMoreActive = filteredMoreItems.some((item) => location.pathname.startsWith(item.to))

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('token_exp')
    localStorage.removeItem('userId')
    window.location.href = '/login'
  }

  return (
    <>
      {/* Menu "Plus" (overlay) */}
      {showMoreMenu && (
        <div className="bottom-nav-more-overlay" onClick={() => setShowMoreMenu(false)}>
          <div className="bottom-nav-more-menu" onClick={(e) => e.stopPropagation()}>
            <div className="bottom-nav-more-header">
              <span>{t('nav:bottom_nav.more_options')}</span>
              <button
                className="bottom-nav-more-close"
                onClick={() => setShowMoreMenu(false)}
                aria-label={t('common:close')}
              >
                <CIcon icon={cilX} size="lg" />
              </button>
            </div>
            <div className="bottom-nav-more-items">
              {filteredMoreItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `bottom-nav-more-item ${isActive ? 'active' : ''}`}
                  onClick={() => setShowMoreMenu(false)}
                >
                  {/* icon = élément JSX déjà rendu (issu de _nav.js) */}
                  <span className="bottom-nav-more-icon">{item.icon}</span>
                  <span>{item.name}</span>
                </NavLink>
              ))}
              <button className="bottom-nav-more-item logout" onClick={handleLogout}>
                <CIcon icon={cilAccountLogout} size="lg" />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barre de navigation principale */}
      <nav className="bottom-navigation">
        {filteredMainItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `bottom-nav-item ${isActive ? 'active' : ''}`}
          >
            <CIcon icon={item.icon} className="bottom-nav-icon" />
            <span className="bottom-nav-label">{item.label}</span>
          </NavLink>
        ))}

        {/* Bouton "Plus" */}
        <button
          className={`bottom-nav-item bottom-nav-more-btn ${isMoreActive || showMoreMenu ? 'active' : ''}`}
          onClick={() => setShowMoreMenu(!showMoreMenu)}
          aria-label={t('nav:bottom_nav.more_options')}
        >
          <CIcon icon={cilMenu} className="bottom-nav-icon" />
          <span className="bottom-nav-label">{t('nav:bottom_nav.more')}</span>
        </button>
      </nav>
    </>
  )
}

export default BottomNavigation
